import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useCohortListQuery } from '@verantos-dev/data-access-cohorts';
import { useDeleteProjectMutation, useGetProjectQuery } from '@verantos-dev/data-access-projects';
import { ApiWorkflowDialogMessages, DialogButton } from '@verantos-dev/shared-components';

import { useProjectModalContext } from '../context/modalContext';

export const useDeleteProjectModal = () => {
  const intl = useIntl();
  const { itemId, closeDialog } = useProjectModalContext();
  const { data, isLoading: loadingProject } = useGetProjectQuery((itemId || '') as string, {
    skip: !itemId,
  });
  const [deleteMutation, { isLoading, error }] = useDeleteProjectMutation();
  const { data: projectCohorts, isLoading: loadingCohorts } = useCohortListQuery({
    projectId: (itemId || '') as string,
    enabled: !!itemId,
  });
  const [saveButtonColor, setSaveButtonColor] = useState<DialogButton['color']>('destructive');

  const hasCohorts = Boolean(projectCohorts?.length);

  const onSubmit = async () => {
    await deleteMutation({ projectId: itemId as string });
    setSaveButtonColor('primary');
  };

  const contentMessages = {
    title: intl.formatMessage({ id: 'projectsList.deleteProjectModal.title' }),
    description: intl.formatMessage(
      { id: 'projectsList.deleteProjectModal.description' },
      { projectId: <b>{data?.data?.projectName || 'project'}</b> }
    ),
    confirmTitle: intl.formatMessage({ id: 'projectsList.deleteProjectModal.confirmTitle' }),
    actionUndone: intl.formatMessage({ id: 'projectsList.deleteProjectModal.actionUndone' }),
    deleteCohorts: intl.formatMessage(
      { id: 'projectsList.deleteProjectModal.deleteCohorts' },
      { count: projectCohorts?.length || 0 }
    ),
  };

  const dialogMessages: ApiWorkflowDialogMessages = {
    loadingCancelButtonText: intl.formatMessage({
      id: 'projectsList.deleteProjectModal.cancelButton',
    }),
    loadingSaveButtonText: intl.formatMessage({
      id: 'projectsList.deleteProjectModal.deleteButton',
    }),
    entryCancelButtonText: intl.formatMessage({
      id: 'projectsList.deleteProjectModal.cancelButton',
    }),
    entrySaveButtonText: intl.formatMessage({ id: 'projectsList.deleteProjectModal.deleteButton' }),
    submittingCancelButtonText: intl.formatMessage({
      id: 'projectsList.deleteProjectModal.cancelButton',
    }),
    submittingSaveButtonText: intl.formatMessage({
      id: 'projectsList.deleteProjectModal.submitting',
    }),
    successCloseButtonText: intl.formatMessage({
      id: 'projectsList.deleteProjectModal.closeButton',
    }),
    submittingErrorTitle: intl.formatMessage({
      id: 'projectsList.deleteProjectModal.submittingError',
    }),
    successTitle: intl.formatMessage({ id: 'projectsList.deleteProjectModal.success.title' }),
    successText: intl.formatMessage(
      { id: 'projectsList.deleteProjectModal.success.text' },
      { projectName: data?.data?.projectName || 'Project' }
    ),
  };

  return {
    dialogMessages,
    contentMessages,
    hasCohorts,
    isSubmitting: isLoading,
    deleteProjectError: error,
    isInitialLoading: loadingProject || loadingCohorts,
    saveButtonColor,
    isValidForSubmit: !hasCohorts && !loadingProject && !loadingCohorts && !isLoading,
    onClose: closeDialog,
    onSubmit,
  };
};
